/* =================================================================================================
 * Pop-up "roue de la fortune" newsletter — Zanaé
 *
 * Parcours en 3 étapes (maquettes pop-up-visu1/2/3.png) :
 *   1. la roue tourne lentement ; au clic sur le bouton elle s'emballe puis
 *      s'arrête sur le segment gagnant configuré dans l'éditeur de thème ;
 *   2. annonce du lot + saisie de l'e-mail (crée un client Shopify taggé
 *      `prospect,newsletter` — c'est Mailchimp qui envoie ensuite l'e-mail de
 *      bienvenue avec le code, cf. CLAUDE.md) ;
 *   3. affichage du code de réduction.
 *
 * L'envoi du formulaire tente l'AJAX pour rester dans la pop-up. En pratique le
 * captcha Shopify le refuse (400 « Missing CAPTCHA token ») : on retombe sur
 * l'envoi natif et l'étape 3 est rejouée au rechargement grâce au paramètre
 * `customer_posted=true`. L'événement GA4 `newsletter_signup` de ce retour est
 * envoyé par custom.js (source mémorisée à l'envoi, cf. `data-signup-source`).
 * ===============================================================================================*/

(function () {
  'use strict';

  var STORAGE_PREFIX = 'zanae_wheel_';

  function setFlag(name, days) {
    var payload = { v: 1, exp: days > 0 ? Date.now() + days * 864e5 : null };
    try {
      window.localStorage.setItem(STORAGE_PREFIX + name, JSON.stringify(payload));
    } catch (e) {
      /* navigation privée / stockage bloqué : la pop-up se réaffichera, sans casse */
    }
  }

  function clearFlag(name) {
    try {
      window.localStorage.removeItem(STORAGE_PREFIX + name);
    } catch (e) {
      /* rien à faire */
    }
  }

  function getFlag(name) {
    try {
      var raw = window.localStorage.getItem(STORAGE_PREFIX + name);
      if (!raw) return false;
      var payload = JSON.parse(raw);
      if (payload.exp && Date.now() > payload.exp) {
        window.localStorage.removeItem(STORAGE_PREFIX + name);
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  function track(name, params) {
    if (typeof window.zanaeTrack === 'function') {
      window.zanaeTrack(name, params);
      return;
    }
    // Au chargement, ce script passe avant custom.js (qui définit zanaeTrack) :
    // les événements des réouvertures immédiates attendent qu'il soit là.
    document.addEventListener('DOMContentLoaded', function () {
      if (typeof window.zanaeTrack === 'function') window.zanaeTrack(name, params);
    }, { once: true });
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Angle de rotation courant d'un élément, en degrés (0-360).
   * @param {HTMLElement} el
   * @returns {number}
   */
  function currentAngle(el) {
    var transform = window.getComputedStyle(el).transform;
    if (!transform || transform === 'none') return 0;
    var values = transform.match(/matrix\(([^)]+)\)/);
    if (!values) return 0;
    var parts = values[1].split(',').map(parseFloat);
    var deg = (Math.atan2(parts[1], parts[0]) * 180) / Math.PI;
    return (deg + 360) % 360;
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /**
   * Éléments focusables contenus dans un élément.
   * À ne pas remplacer par une concaténation `'<parent> ' + FOCUSABLE` : la
   * portée ne s'appliquerait qu'au premier terme de la liste, les suivants
   * balaieraient toute la page.
   * @param {?Element} root
   * @returns {Array<HTMLElement>}
   */
  function focusableIn(root) {
    return root ? Array.from(root.querySelectorAll(FOCUSABLE)) : [];
  }

  if (customElements.get('wheel-popup')) return;

  class WheelPopup extends HTMLElement {
    connectedCallback() {
      this.window = this.querySelector('.wheel-popup__window');
      this.wheel = this.querySelector('.wheel-popup__wheel');
      this.steps = Array.from(this.querySelectorAll('.wheel-popup__step'));
      this.form = this.querySelector('.wheel-popup__form');
      this.dismissDays = parseInt(this.dataset.dismissDays, 10) || 0;
      this.hasSpun = false;

      this.onKeydown = this.handleKeydown.bind(this);

      this.addEventListener('click', this.handleClick.bind(this));
      if (this.form) this.form.addEventListener('submit', this.handleSubmit.bind(this));

      // Dès le départ, seules les commandes de l'étape visible sont actives.
      this.steps.forEach((step) => {
        this.activerChamps(step, step.hasAttribute('data-active'));
      });

      if (window.Shopify && window.Shopify.designMode) {
        // Dans l'éditeur : la pop-up suit la sélection de section, sans mémoriser de fermeture.
        document.addEventListener('shopify:section:select', (evt) => {
          if (evt.target.contains(this)) this.open();
        });
        document.addEventListener('shopify:section:deselect', () => this.close(true));
        return;
      }

      // Le formulaire est revenu en erreur après un envoi natif : on rouvre sur
      // l'étape de saisie, sinon le message resterait invisible derrière la roue.
      if (this.querySelector('.wheel-popup__form .errors')) {
        window.requestAnimationFrame(() => {
          this.open(null, true);
          this.goTo('prize');
        });
        return;
      }

      // Retour d'un envoi natif du formulaire (fallback sans JS/fetch) : on rouvre à l'étape 3.
      if (window.location.search.indexOf('customer_posted=true') !== -1 && getFlag('pending')) {
        clearFlag('pending');
        setFlag('done', 365);
        this.open(null, true);
        this.goTo('code');
        this.applyDiscount();
        return;
      }

      // Aide au test : `?wheel` ouvre tout de suite, sans tenir compte des
      // fermetures mémorisées ni du délai. `?wheel=prize` et `?wheel=code`
      // ouvrent directement l'étape voulue, pour en valider le rendu sans
      // rejouer tout le parcours.
      var forced = window.location.search.match(/[?&]wheel(?:=([a-z]*))?(?:&|$)/);
      if (forced) {
        var step = forced[1];
        window.requestAnimationFrame(() => {
          this.open();
          if (step === 'prize' || step === 'code') this.goTo(step);
        });
        return;
      }

      if (getFlag('done') || getFlag('dismissed')) return;
      if (this.dataset.showOnMobile === 'false' && window.matchMedia('(max-width: 749px)').matches) return;

      var delay = parseInt(this.dataset.delay, 10);
      window.setTimeout(() => this.open(), (isNaN(delay) ? 5 : delay) * 1000);
    }

    /**
     * Active ou neutralise les champs d'une étape.
     *
     * Un champ obligatoire dans une étape masquée par `visibility` reste
     * validé par le navigateur : ne pouvant pas l'afficher pour le signaler,
     * il pose sa bulle « Veuillez renseigner ce champ » n'importe où et bloque
     * l'envoi. Désactiver le champ le fait ignorer — c'est le seul état, avec
     * `display: none`, que la validation saute.
     * @param {Element} step
     * @param {boolean} actif
     */
    activerChamps(step, actif) {
      step.querySelectorAll('input, select, textarea, button').forEach((champ) => {
        champ.disabled = !actif;
      });
    }

    /**
     * L'étape actuellement visible.
     * @returns {?Element}
     */
    activeStep() {
      return this.querySelector('.wheel-popup__step[data-active]');
    }

    /* ------------------------------------------------------------ ouverture */

    /**
     * @param {?Element} trigger - Lien déclencheur (non utilisé, transmis par custom.js).
     * @param {boolean} [technical] - Réouverture technique (retour d'envoi, erreur) :
     *   ce n'est pas un nouvel affichage, `newsletter_popup_opened` n'est pas compté.
     */
    open(trigger, technical) {
      if (this.hasAttribute('open')) return;
      this.lastFocused = document.activeElement;
      this.setAttribute('open', '');
      document.addEventListener('keydown', this.onKeydown);
      var target = focusableIn(this.activeStep())[0] || this.window;
      window.requestAnimationFrame(() => target.focus({ preventScroll: true }));
      if (!technical) track('newsletter_popup_opened');
    }

    close(silent) {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      document.removeEventListener('keydown', this.onKeydown);
      if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus({ preventScroll: true });
      if (!silent) {
        if (this.dismissDays > 0) setFlag('dismissed', this.dismissDays);
        track('newsletter_popup_closed');
      }
    }

    handleKeydown(evt) {
      if (evt.key === 'Escape') {
        this.close();
        return;
      }
      if (evt.key !== 'Tab') return;

      // Piège à focus : on boucle sur les éléments focusables de l'étape visible.
      var items = focusableIn(this.activeStep())
        .concat(Array.from(this.querySelectorAll('.wheel-popup__close')));
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    }

    handleClick(evt) {
      if (evt.target.closest('.js-wheel-close, .wheel-popup__backdrop')) {
        evt.preventDefault();
        this.close();
      } else if (evt.target.closest('.js-wheel-spin')) {
        evt.preventDefault();
        this.spin(evt.target.closest('.js-wheel-spin'));
      }
    }

    /* ----------------------------------------------------------------- roue */

    /**
     * Lance la roue et l'arrête sur le segment gagnant, en deux temps :
     *   1. plusieurs tours rapides à vitesse constante (transition linéaire) ;
     *   2. un ralentissement progressif qui termine pile sous le pointeur.
     *
     * Les segments sont numérotés dans le sens horaire depuis le haut :
     * 0 = haut, 1 = droite, 2 = bas, 3 = gauche. Le pointeur est en haut, donc
     * amener le segment `i` sous le pointeur demande une rotation ≡ (360 - i×90).
     *
     * La courbe du temps 2 démarre à une vitesse proche de celle du temps 1
     * (pente initiale ≈ 4×) pour que le raccord ne se voie pas.
     * @param {HTMLElement} button - Bouton déclencheur.
     */
    spin(button) {
      if (this.hasSpun) return;
      this.hasSpun = true;
      button.disabled = true;

      var index = parseInt(this.dataset.winningSegment, 10) || 0;
      var from = currentAngle(this.wheel);
      var finalMod = (360 - index * 90) % 360;
      var partial = (((finalMod - from) % 360) + 360) % 360;

      track('wheel_spun', { segment: index });

      this.wheel.setAttribute('data-spinning', '');
      this.wheel.style.transition = 'none';
      this.wheel.style.transform = 'rotate(' + from + 'deg)';
      void this.wheel.offsetWidth; // force le reflow pour figer le point de départ

      // Mouvement réduit : on se pose sans spectacle.
      if (prefersReducedMotion()) {
        this.wheel.style.transition = 'transform 600ms ease-out';
        this.wheel.style.transform = 'rotate(' + (from + partial + 360) + 'deg)';
        this.finishSpin(1050);
        return;
      }

      var fastTurns = parseInt(this.dataset.fastTurns, 10) || 4;
      var fastMs = fastTurns * ((parseFloat(this.dataset.fastTurnDuration) || 0.4) * 1000);
      var slowTurns = parseInt(this.dataset.slowTurns, 10) || 2;
      var slowMs = (parseFloat(this.dataset.slowDuration) || 3.6) * 1000;

      var fastEnd = from + 360 * fastTurns;
      var to = fastEnd + partial + 360 * slowTurns;

      // Temps 1 — vitesse constante.
      this.wheel.style.transition = 'transform ' + fastMs + 'ms linear';
      this.wheel.style.transform = 'rotate(' + fastEnd + 'deg)';

      // Temps 2 — décélération jusqu'au segment gagnant.
      window.setTimeout(() => {
        this.wheel.style.transition = 'transform ' + slowMs + 'ms cubic-bezier(0.1, 0.4, 0.2, 1)';
        this.wheel.style.transform = 'rotate(' + to + 'deg)';
      }, fastMs);

      this.finishSpin(fastMs + slowMs + 450);
    }

    /**
     * Marque la roue comme arrêtée puis enchaîne sur l'étape du lot.
     * @param {number} delay - Attente en millisecondes.
     */
    finishSpin(delay) {
      window.setTimeout(() => {
        this.wheel.removeAttribute('data-spinning');
        this.wheel.setAttribute('data-settled', '');
        this.goTo('prize');
      }, delay);
    }

    /* --------------------------------------------------------------- étapes */

    goTo(name) {
      this.steps.forEach((step) => {
        var actif = step.dataset.step === name;
        if (actif) step.setAttribute('data-active', '');
        else step.removeAttribute('data-active');
        this.activerChamps(step, actif);
      });
      var focusable = focusableIn(this.activeStep())[0];
      if (focusable) window.requestAnimationFrame(() => focusable.focus({ preventScroll: true }));
      track('wheel_popup_step', { step: name });
    }

    /* -------------------------------------------------------- code au panier */

    /**
     * Dépose le code de réduction dans le panier via le permalien Shopify
     * `/discount/<code>` — le même mécanisme que le champ code du cart drawer
     * (cf. `zanaeApplyDiscount` dans custom.js), qui a l'avantage d'être
     * reflété par le Liquid du panier.
     *
     * Sur un panier vide, le code n'apparaît pas encore dans `discount_codes`
     * mais reste mémorisé par Shopify et s'appliquera au paiement : ce n'est
     * donc pas un échec. Et comme le visiteur a de toute façon son code sous
     * les yeux à l'étape 3, un problème ici ne doit rien interrompre.
     */
    async applyDiscount() {
      var code = this.dataset.discountCode;
      if (!code || this.dataset.autoApply !== 'true') return;

      try {
        var res = await fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', {
          headers: { Accept: 'application/json' },
        });
        var cart = await res.json();
        var inCart = (cart.discount_codes || []).some((d) => (
          d.code && d.code.toLowerCase() === code.toLowerCase()
        ));

        track('wheel_discount_applied', { code: code, in_cart: inCart });

        // Le récap du drawer (Réduction / Total) doit se recalculer.
        if (inCart) {
          document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        }
      } catch (e) {
        /* sans effet sur le parcours : le code reste affiché à l'étape 3 */
      }
    }

    /* ----------------------------------------------------------- formulaire */

    async handleSubmit(evt) {
      var input = this.form.querySelector('.wheel-popup__input');
      var errorEl = this.form.querySelector('.wheel-popup__error');
      var button = this.form.querySelector('.wheel-popup__btn');

      if (!input || !input.value || input.validity.valid === false) return; // laisse le navigateur signaler

      evt.preventDefault();
      errorEl.textContent = '';
      button.disabled = true;

      // Repère pour le fallback : si le fetch échoue et que le formulaire part en
      // navigation classique, le rechargement rouvrira la pop-up sur l'étape 3.
      setFlag('pending', 1);

      try {
        var response = await fetch(this.form.action, {
          method: 'POST',
          body: new FormData(this.form),
          headers: { Accept: 'text/html' },
        });
        var body = await response.text();

        // 1. Erreur de formulaire explicitement renvoyée par Shopify — le seul
        //    cas où l'on affiche un message rouge. Le balisage vient de
        //    `form-errors.liquid`, rendu dans le formulaire de la pop-up.
        var doc = new DOMParser().parseFromString(body, 'text/html');
        var errors = Array.from(doc.querySelectorAll('.errors .errors-text li'))
          .map((li) => li.textContent.trim())
          .filter(Boolean);
        if (errors.length) {
          button.disabled = false;
          errorEl.textContent = errors.join(' ');
          return;
        }

        // 2. Réponse correcte : c'est passé.
        if (response.ok) {
          clearFlag('pending');
          setFlag('done', 365);
          // Envoyé ici et non au retour de page : on retire la source mémorisée par custom.js.
          try { window.sessionStorage.removeItem('zanae_signup_source'); } catch (e) { /* rien */ }
          track('newsletter_signup', { signup_source: 'wheel_popup', method: 'ajax' });
          this.goTo('code');
          this.applyDiscount();
          return;
        }

        // 3. Tout le reste : le captcha Shopify refuse l'envoi programmatique
        //    (400 « Missing CAPTCHA token » — cas systématique constaté le
        //    01/10/2026), ou Cloudflare (403 `cf-mitigated: challenge`, 429).
        //    On laisse donc le navigateur renvoyer le formulaire normalement :
        //    le script captcha de Shopify y ajoute son jeton, puis Shopify
        //    revient avec `customer_posted=true` et la pop-up se rouvre sur
        //    l'étape 3. Jamais de message rouge ici.
        window.console.warn('[roue] envoi AJAX refusé (statut ' + response.status + '), bascule en envoi natif');
        this.form.submit();
      } catch (error) {
        // Le fetch lui-même n'a pas abouti : même repli, même raison.
        window.console.warn('[roue] fetch impossible, bascule en envoi natif :', error);
        this.form.submit();
      }
    }
  }

  customElements.define('wheel-popup', WheelPopup);
})();
